/**
 * The agent sign-in link landing page — Step 155.
 *
 * The same shape as the portal's, and for the same reason: redemption is a
 * POST from here, never the GET itself. Mail security scanners prefetch every
 * link in every email, so a GET that consumed the token would burn each link
 * before the human ever saw it.
 *
 * Separate from `PortalRedeemPage` rather than shared, because the two doors
 * post to different endpoints, land on different surfaces, and recover to
 * different login pages. Folding them into one component parameterised three
 * ways would put the agent and requester auth domains in a single file, which
 * is exactly the crossing the whole design forbids.
 *
 * Bare fetch on purpose: agent auth-domain plumbing. `apiFetch`'s 401 redirect
 * would bounce this page to the login page it is trying to complete.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../lib/urlState';
import { useAuth } from '../lib/AuthContext';

export function LoginRedeemPage() {
  const [tokenParam] = useUrlState('token', null);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false); // StrictMode double-mount must not double-redeem

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = tokenParam ?? '';
    fetch('/auth/link/redeem', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setError(body?.error ?? 'That link did not work.');
          return;
        }
        await refresh();
        navigate(body?.redirectTo ?? '/dashboard', { replace: true });
      })
      .catch(() => setError('Could not reach the server — try the link again.'));
  }, [tokenParam, navigate, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center">
        {error ? (
          <>
            <p className="text-sm font-medium text-destructive">{error}</p>
            <a className="mt-3 inline-block text-sm text-brand hover:underline" href="/login">
              Request a fresh link
            </a>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
